/** Dropdown lists (§4.2.3): add values without touching code. */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import {
  Alert,
  Button,
  Card,
  Field,
  Modal,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
} from '../../components/ui';

interface ParameterValue {
  id: string;
  code: string;
  label: string;
  sortOrder: number;
  active: boolean;
  meta: Record<string, unknown> | null;
}

interface ParameterGroup {
  id: string;
  key: string;
  label: string;
  isSystem: boolean;
  values: ParameterValue[];
}

export function ParametersPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [adding, setAdding] = useState<ParameterGroup | null>(null);
  const [form, setForm] = useState({ code: '', label: '', probability: '' });
  const [error, setError] = useState<string | null>(null);

  const groups = useQuery({
    queryKey: ['parameters', 'all'],
    queryFn: () => api<{ data: ParameterGroup[] }>('/parameters?activeOnly=false'),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['parameters'] });
  };

  const addValue = useMutation({
    mutationFn: (group: ParameterGroup) =>
      api(`/parameters/groups/${group.id}/values`, {
        method: 'POST',
        body: {
          code: form.code.trim().toUpperCase(),
          label: form.label.trim(),
          sortOrder: (group.values.length + 1) * 10,
          ...(group.key === 'lead_stage' && form.probability
            ? { meta: { probability: Number(form.probability) / 100 } }
            : {}),
        },
      }),
    onSuccess: () => {
      setError(null);
      setAdding(null);
      setForm({ code: '', label: '', probability: '' });
      invalidate();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not add the value.'),
  });

  const toggleActive = useMutation({
    mutationFn: (value: ParameterValue) =>
      api(`/parameters/values/${value.id}`, { method: 'PATCH', body: { active: !value.active } }),
    onSuccess: invalidate,
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not update the value.'),
  });

  if (groups.isLoading) return <Skeleton className="h-96 w-full" />;

  const list = groups.data?.data ?? [];
  const current = list.find((group) => group.id === selected) ?? list[0];

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <header>
        <h1 className="text-xl font-bold text-navy dark:text-white">Parameters</h1>
        <p className="text-sm text-slate-500">
          The dropdown lists used across the system. Disable a value to keep it on old records while
          hiding it from new ones.
        </p>
      </header>

      {error && <Alert tone="critical">{error}</Alert>}

      <div className="grid gap-5 lg:grid-cols-[260px_1fr]">
        <Card>
          <ul className="space-y-1">
            {list.map((group) => (
              <li key={group.id}>
                <button
                  onClick={() => setSelected(group.id)}
                  className={`w-full rounded-xl px-3 py-2 text-left text-sm ${
                    current?.id === group.id
                      ? 'bg-navy font-semibold text-white'
                      : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                  }`}
                >
                  {group.label}
                  <span className="ml-1 text-xs opacity-70">({group.values.length})</span>
                </button>
              </li>
            ))}
          </ul>
        </Card>

        {current && (
          <Card
            title={current.label}
            description={`List key: ${current.key}`}
            actions={
              <Button size="sm" onClick={() => setAdding(current)}>
                <Plus aria-hidden="true" className="h-4 w-4" /> Add value
              </Button>
            }
          >
            <TableWrap>
              <thead>
                <tr>
                  <Th>Code</Th>
                  <Th>Label</Th>
                  <Th>Extra</Th>
                  <Th align="right">Status</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {current.values.map((value) => (
                  <tr key={value.id}>
                    <Td className="font-mono text-xs">{value.code}</Td>
                    <Td>{value.label}</Td>
                    <Td className="text-xs text-slate-500">
                      {value.meta && typeof value.meta.probability === 'number'
                        ? `${Math.round(value.meta.probability * 100)}% probability`
                        : value.meta && typeof value.meta.days === 'number'
                          ? `${value.meta.days} days`
                          : value.meta && value.meta.government === true
                            ? 'Government client'
                            : '—'}
                    </Td>
                    <Td align="right">
                      <button onClick={() => toggleActive.mutate(value)} title="Click to change">
                        <StatusChip tone={value.active ? 'ok' : 'neutral'}>
                          {value.active ? 'Active' : 'Hidden'}
                        </StatusChip>
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          </Card>
        )}
      </div>

      {adding && (
        <Modal title={`Add to ${adding.label}`} onClose={() => setAdding(null)}>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              addValue.mutate(adding);
            }}
          >
            <Field label="Code" hint="Short and unique in this list, e.g. NET45." required>
              <input
                className={inputClass}
                required
                value={form.code}
                onChange={(event) => setForm({ ...form, code: event.target.value })}
              />
            </Field>
            <Field label="Label" hint="What people see in the dropdown." required>
              <input
                className={inputClass}
                required
                value={form.label}
                onChange={(event) => setForm({ ...form, label: event.target.value })}
              />
            </Field>
            {adding.key === 'lead_stage' && (
              <Field label="Probability (%)" hint="Used for the weighted pipeline.">
                <input
                  className={inputClass}
                  type="number"
                  min={0}
                  max={100}
                  value={form.probability}
                  onChange={(event) => setForm({ ...form, probability: event.target.value })}
                />
              </Field>
            )}
            <div className="flex gap-2">
              <Button type="submit" busy={addValue.isPending}>
                Add value
              </Button>
              <Button type="button" variant="secondary" onClick={() => setAdding(null)}>
                Cancel
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
